import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { inviteUser, inviteUsersBulk } from '@/store/auth/auth.api';
import { UserRole } from '@/utils/roles';
import { toast } from '@/utils/toast';

import {
  acceptInvitation,
  answerEventInvite,
  createInviteCode,
  deleteEventInviteAnswer,
  deleteInvite,
  generateInviteLink,
  getEventInviteAnswer,
  getEventInviteAnswers,
  getEventInviteStatus,
  getInviteById,
  getInviteCodes,
  getInvites,
  getSeatAvailability,
  invite,
  joinByInviteCode,
  updateInviteDeadline,
} from './invites.api';
import type {
  CreateInvitation,
  EventInviteAnswer,
  EventInviteAnswerChoice,
  GenerateInviteLinkRequest,
  InviteCodeRole,
  InviteEvent,
  SingleInvite,
} from './invites.types';

export interface PendingGuestInvite {
  id: string;
  /** Every live row for this invitee: withdrawing only `id` would leave them invited. */
  ids: string[];
  /** Set for someone who already has an account — their invite carries no contact. */
  personId?: string;
  contact: string;
  name: string;
  message?: string;
  /** When it was sent. */
  createdAt: string;
  /** The deadline to answer. */
  expiresAt?: string;
  /** Who sent it: they may handle it without running the vessel. */
  creatorId?: string;
}

/**
 * The guests invited to an event who have not answered yet: the invite row is
 * deleted on accept, at which point the person is a participant instead. The
 * backend allows duplicate invite rows for the same invitee+event (no unique
 * constraint), so the same person can come back twice — one entry per person/contact,
 * holding all of their row ids.
 */
export const pendingGuestInvites = (
  invites: SingleInvite[] | undefined,
  participantIds: Set<string>,
  /** Answers contacts gave before registering: their invitations are no longer waiting. */
  answers: EventInviteAnswer[] = [],
): PendingGuestInvite[] => {
  const answered = new Set(answers.map(({ inviteId }) => inviteId));
  const byInvitee = new Map<string, PendingGuestInvite>();
  for (const i of invites ?? []) {
    if (i.role !== UserRole.GUEST || participantIds.has(i.person_id) || answered.has(i.id)) {
      continue;
    }
    const key = i.person_id || i.contact;
    const seen = byInvitee.get(key);
    if (seen) {
      seen.ids.push(i.id);
      continue;
    }
    byInvitee.set(key, {
      id: i.id,
      ids: [i.id],
      personId: i.person_id || undefined,
      contact: i.contact,
      name: i.name,
      message: i.message,
      createdAt: i.created_at,
      expiresAt: i.expires_at,
      creatorId: i.creator_id,
    });
  }
  return [...byInvitee.values()];
};

export const useCreateInvitationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (i: CreateInvitation) => invite(i),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['invites-list'] });
    },
  });
};

export interface GuestInvitee {
  name: string;
  contact: string;
  /** Set for a contact picked from Contacts: they already have an account. */
  personId?: string;
}

/**
 * Invites guests to an event. A contact picked from Contacts is invited by person id, like a past
 * guest: auth matches an account only on its exact login, and the phone or email a contact lists
 * is often not it — the invitee then got a sign-up SMS/email and no in-app invitation, while the
 * sender saw them as Invited. A guest typed in by contact still goes through auth, which signs up
 * a newcomer or finds the account.
 */
export const useInviteGuestsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      invitees,
      vessel,
      event,
    }: {
      invitees: GuestInvitee[];
      vessel: { id: string; vessel_name?: string };
      event: InviteEvent;
    }) => {
      const byContact = invitees.filter(({ personId }) => !personId);
      await Promise.all([
        ...(byContact.length > 0
          ? [
              inviteUsersBulk(
                byContact.map(({ contact, name }) => ({ contact, name })),
                UserRole.GUEST,
                vessel.id,
                'vessel',
                vessel.vessel_name,
                event,
              ),
            ]
          : []),
        ...invitees.flatMap(({ personId, name }) =>
          personId
            ? [
                invite({
                  personId,
                  name,
                  role: UserRole.GUEST,
                  serviceId: vessel.id,
                  serviceType: 'vessel',
                  vesselName: vessel.vessel_name ?? '',
                  event,
                }),
              ]
            : [],
        ),
      ]);
    },
    onSettled() {
      // Settled, not success: one invitee failing still leaves the others invited.
      queryClient.invalidateQueries({ queryKey: ['invites-list'] });
    },
  });
};

export const useSingleInviteQuery = (id: string) =>
  useQuery({
    queryKey: ['invites', id],
    queryFn: () => getInviteById(id),
    enabled: !!id,
  });

export const useAcceptInviteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => acceptInvitation(id),
    onSuccess(_, id) {
      queryClient.invalidateQueries({ queryKey: ['invites', id] });
    },
  });
};

export const useInvitesQuery = (params: {
  person_id?: string;
  service_id?: string;
  event_id?: string;
}) =>
  useQuery({
    queryKey: ['invites-list', params.person_id, params.service_id, params.event_id],
    queryFn: () => getInvites({ page: 1, limit: 100, ...params }),
    enabled: !!(params.person_id || params.service_id || params.event_id),
  });

/**
 * The invites on one event. Both ids are required: without service_id the backend
 * scopes the list to the caller's own invites, so the owner would always see none.
 * Shares useInvitesQuery's key so the cache and its invalidation are the same.
 */
export const useEventInvitesQuery = (eventId?: string, vesselId?: string) =>
  useQuery({
    queryKey: ['invites-list', undefined, vesselId, eventId],
    queryFn: () => getInvites({ page: 1, limit: 100, event_id: eventId, service_id: vesselId }),
    enabled: !!eventId && !!vesselId,
  });

/** What the contact holding a preview link answered; null until they answer. */
export const useEventInviteAnswerQuery = (previewId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['invite-answer', previewId],
    queryFn: () => getEventInviteAnswer(previewId),
    enabled: enabled && !!previewId,
    retry: false,
  });

/** The invitation's deadline, or why it can no longer be answered; null once it is gone. */
export const useEventInviteStatusQuery = (previewId: string) =>
  useQuery({
    queryKey: ['invite-status', previewId],
    queryFn: () => getEventInviteStatus(previewId),
    enabled: !!previewId,
    retry: false,
  });

export const useAnswerEventInviteMutation = (previewId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ answer, reason }: { answer: EventInviteAnswerChoice; reason?: string }) =>
      answerEventInvite(previewId, answer, reason),
    onSuccess(state) {
      queryClient.setQueryData(['invite-answer', previewId], state);
    },
  });
};

/** Answers contacts without an account gave to the event's invitations. */
export const useEventInviteAnswersQuery = (eventId?: string, vesselId?: string) =>
  useQuery({
    queryKey: ['invite-answers', vesselId, eventId],
    queryFn: () => getEventInviteAnswers(vesselId ?? '', eventId ?? ''),
    enabled: !!eventId && !!vesselId,
  });

export const useDeleteEventInviteAnswerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteEventInviteAnswer(id),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['invite-answers'] });
      queryClient.invalidateQueries({ queryKey: ['invites-list'] });
    },
  });
};

export const useUpdateInviteDeadlineMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, expiresAt }: { id: string; expiresAt: string }) =>
      updateInviteDeadline(id, expiresAt),
    onSuccess(_, { id }) {
      queryClient.invalidateQueries({ queryKey: ['invites-list'] });
      queryClient.invalidateQueries({ queryKey: ['invites', id] });
    },
  });
};

export const useDeclineInviteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => deleteInvite(id, reason),
    onSuccess(_, { id }) {
      queryClient.invalidateQueries({ queryKey: ['invites-list'] });
      queryClient.invalidateQueries({ queryKey: ['invites', id] });
    },
  });
};

export const inviteCodesKey = (vesselId: string | undefined) => ['invite-codes', vesselId] as const;

/** The vessel's pending codes. Only its owner group and captain may read them. */
export const useInviteCodesQuery = (vesselId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: inviteCodesKey(vesselId),
    queryFn: () => getInviteCodes(vesselId!),
    enabled: enabled && !!vesselId,
  });

export const useCreateInviteCodeMutation = (vesselId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (role: InviteCodeRole) => createInviteCode(vesselId, role),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: inviteCodesKey(vesselId) }),
  });
};

export const useRevokeInviteCodeMutation = (vesselId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => deleteInvite(inviteId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: inviteCodesKey(vesselId) }),
  });
};

export const useJoinByInviteCodeMutation = () =>
  useMutation({
    mutationFn: (code: string) => joinByInviteCode(code),
  });

/**
 * There is no resend endpoint: an invitation is sent when it is created. So a resend is a
 * fresh invitation — a new in-app notification, or a new email/SMS for someone without an
 * account, with a new expiry. The backend drops the person's older invitations to the same
 * vessel and event as it stores the new one.
 */
export const useResendInviteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      pending,
      role,
      roleName,
      event,
      vesselId,
      vesselName,
    }: {
      pending: PendingGuestInvite;
      role: string;
      roleName?: string;
      event?: InviteEvent;
      vesselId: string;
      vesselName?: string;
    }) => {
      if (pending.personId) {
        await invite({
          personId: pending.personId,
          role,
          roleName,
          name: pending.name,
          serviceId: vesselId,
          serviceType: 'vessel',
          vesselName: vesselName ?? '',
          event,
          message: pending.message,
        });
      } else {
        await inviteUser(
          pending.contact,
          pending.name,
          role,
          vesselId,
          'vessel',
          vesselName,
          roleName,
          event,
          pending.message,
        );
      }
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['invites-list'] });
    },
  });
};

/**
 * Cancel, Resend and Change RSVP Deadline for a pending guest invitation, wherever one is listed. Without an
 * `event` it is an invitation onto the vessel itself.
 */
export const useGuestInviteActions = () => {
  // Same DELETE as an invitee's decline; from the sender it is a withdrawal and notifies nobody.
  const { mutateAsync: withdraw } = useDeclineInviteMutation();
  const { mutateAsync: resendInvite, isPending: isResending } = useResendInviteMutation();
  const { mutateAsync: moveDeadline } = useUpdateInviteDeadlineMutation();

  const cancel = async (invite: PendingGuestInvite) => {
    try {
      await Promise.all(invite.ids.map((id) => withdraw({ id })));
      toast.success('Invitation cancelled');
    } catch (error) {
      console.error('Cancel invitation error:', error);
      toast.error('Couldn’t cancel the invitation. Please try again.');
    }
  };

  const resend = async (
    invite: PendingGuestInvite,
    vessel: { id: string; vessel_name?: string },
    event?: InviteEvent,
  ) => {
    try {
      await resendInvite({
        pending: invite,
        role: UserRole.GUEST,
        event,
        vesselId: vessel.id,
        vesselName: vessel.vessel_name,
      });
      toast.success('Invitation resent');
    } catch (error) {
      console.error('Resend invitation error:', error);
      toast.error('Couldn’t resend the invitation. Please try again.');
    }
  };

  /** Rejects after the error toast, so the caller can keep its form open. */
  const changeDeadline = async (invite: PendingGuestInvite, expiresAt: string) => {
    try {
      await Promise.all(invite.ids.map((id) => moveDeadline({ id, expiresAt })));
      toast.success('The RSVP deadline has been changed.');
    } catch (error) {
      console.error('Change RSVP deadline error:', error);
      toast.error('Couldn’t change the deadline. Please try again.');
      throw error;
    }
  };

  return { cancel, resend, changeDeadline, isResending };
};

export const useGenerateInviteLink = () =>
  useMutation({
    mutationFn: (body: GenerateInviteLinkRequest) => generateInviteLink(body),
  });

/** The seat somebody invited in `role` would take on the vessel; asked only once a role is picked. */
export const useSeatAvailabilityQuery = (serviceId?: string, role?: string) =>
  useQuery({
    queryKey: ['seat-availability', serviceId, role],
    queryFn: () => getSeatAvailability(serviceId!, role!),
    enabled: !!serviceId && !!role,
  });
